/**
 * Run W / W-3 — the Integrations section's blocks, as addresses.
 *
 * Q-5 turned IntegrationsPage into a SECTION of ClinicSettingsPage. The tiles
 * kept their old `action` targets, which pointed at the legacy Settings root
 * `/settings/clinic` (→ DEFAULT_SECTION 'identity') and at
 * `/settings/identity`, so every "Configure →" opened Clinic Identity instead
 * of the block it named. The configuration forms themselves were headed blocks
 * with no ids, so there was nothing a link could aim at.
 *
 * Each block now carries `id="integration-<anchor>"`, this module is the one
 * list of those anchors, and the section scrolls the anchor on the URL into
 * view. Kept as data so the tiles, the legacy redirects and the test that pins
 * them all read the same source.
 */

/** Every configuration block inside the Integrations section, in render order. */
export const INTEGRATION_ANCHORS = [
  'frontdesk',
  'reports',
  'ai',
  'email',
  'credits',
  'google',
  'whatsapp',
  'practicehub',
] as const;

export type IntegrationAnchor = (typeof INTEGRATION_ANCHORS)[number];

export const INTEGRATIONS_SECTION_PATH = '/settings/integrations';

/** The DOM id a block carries. */
export const anchorId = (a: IntegrationAnchor): string => `integration-${a}`;

/** The address that opens the section AND scrolls that block into view. */
export const integrationHref = (a: IntegrationAnchor): string =>
  `${INTEGRATIONS_SECTION_PATH}#${anchorId(a)}`;

/**
 * What each Integrations tile's "Configure →" opens.
 *
 * `posturescreen` is null on purpose. PostureScreen is a per-assessment PDF /
 * photo upload (components/posture/PostureUpload.tsx → POST
 * /api/posture-upload, AI extraction); there is no PostureScreen
 * configuration anywhere in the app — no credentials, no endpoint, no clinic
 * setting — so the tile has nothing to open. It stays actionless rather than
 * pointing at a screen that does not configure it.
 */
export const TILE_TARGETS: Record<string, string | null> = {
  email: integrationHref('email'),
  ai: integrationHref('ai'),
  practicehub: integrationHref('practicehub'),
  google: integrationHref('google'),
  posturescreen: null,
};

/**
 * `/integrations/<slug>` — the addresses that existed while the tiles were a
 * top-level page. `/integrations` itself is handled by MOVED_ROUTES; these are
 * the deeper links, which until now matched NO route at all and fell through
 * to the 404 page.
 */
export const LEGACY_INTEGRATION_PATHS: Record<string, string> = {
  email: integrationHref('email'),
  smtp: integrationHref('email'),
  ai: integrationHref('ai'),
  openrouter: integrationHref('ai'),
  credits: integrationHref('credits'),
  'ai-credits': integrationHref('credits'),
  practicehub: integrationHref('practicehub'),
  'practice-hub': integrationHref('practicehub'),
  google: integrationHref('google'),
  reviews: integrationHref('google'),
  'google-reviews': integrationHref('google'),
  whatsapp: integrationHref('whatsapp'),
  frontdesk: integrationHref('frontdesk'),
  'front-desk': integrationHref('frontdesk'),
  reports: integrationHref('reports'),
  templates: integrationHref('reports'),
};

/**
 * Where `/integrations/<slug>` goes. An unknown slug lands on the Integrations
 * section — the thing the address asked for — never on the Settings root.
 */
export const legacyIntegrationTarget = (slug: string | undefined): string =>
  (slug ? LEGACY_INTEGRATION_PATHS[slug] : undefined) ?? INTEGRATIONS_SECTION_PATH;
